import { Box, Button, Callout, Flex, Grid, Heading, Link, RadioCards, Select, Separator, Text, TextField } from '@radix-ui/themes'
import { AlertCircle, Car, Gauge, ImagePlus, Instagram, Mail, MapPinHouse, Phone, User, X } from 'lucide-react';
import { SubmitEvent, useEffect, useMemo } from 'react'
import ColorSwatch from '@/shared/cars/ColorSwatch'
import { AspirationOption, ColorFamilyOption } from '@/types/shared'
import { DriverCarsEditPhoto } from '@/types/driver_cars'

// Mirrors DriverCar::MAX_PHOTOS and PHOTO_CONTENT_TYPES; the model enforces both.
const MAX_PHOTOS = 3
const PHOTO_TYPES = 'image/jpeg,image/png,image/webp'

export interface DriverCarFormData {
  participant: {
    location: string
    email: string
    phone_number: string
    instagram: string
  }
  /** Attachment ids of current photos the driver has marked for removal. */
  remove_photo_ids?: number[]
  driver_car: {
    driver_name: string
    make: string
    model: string
    chassis_code: string
    year: number | null
    trim: string
    engine: string
    aspiration: number | null
    charger: string
    hp: number | null
    torque: number | null
    color_family: string
    body_kit: string
    wheels: string
    /** Files chosen in this visit, attached on save after the kept photos. */
    new_photos?: File[]
  }
}

interface DriverCarFormProps {
  data: DriverCarFormData
  setData: <K extends keyof DriverCarFormData>(key: K, value: DriverCarFormData[K]) => void
  onSubmit: (e: SubmitEvent) => void
  processing: boolean
  errors?: string[]
  title: string
  /** Sits under the title. Defaults to the first-time setup pitch. */
  subtitle?: string
  submitLabel: string
  colorFamilies: ColorFamilyOption[]
  /** DriverCar::ASPIRATIONS, so the select and the public card share wording. */
  aspirations: AspirationOption[]
  /**
   * The car's current photos. Omit to leave the photo section out entirely —
   * the application form does, so applying never uploads anything.
   */
  photos?: DriverCarsEditPhoto[]
  /** Null leaves Cancel out — onboarding has its own Back and Skip. */
  cancelPath?: string | null
}

export default function DriverCarForm({
  data,
  setData,
  onSubmit,
  processing,
  errors,
  title,
  subtitle = 'Fill out this form to make registering your car for events super fast.',
  submitLabel,
  colorFamilies,
  aspirations,
  photos,
  cancelPath = '/garage',
}: DriverCarFormProps) {
  const newPhotos = data.driver_car.new_photos ?? []
  const removeIds = data.remove_photo_ids ?? []
  const keptCount = (photos ?? []).filter((p) => !removeIds.includes(p.id)).length
  const openSlots = MAX_PHOTOS - keptCount - newPhotos.length

  // Local previews for files that haven't been uploaded yet.
  const newPreviews = useMemo(() => newPhotos.map((f) => URL.createObjectURL(f)), [newPhotos])
  useEffect(() => () => newPreviews.forEach((url) => URL.revokeObjectURL(url)), [newPreviews])

  const toggleRemove = (id: number) =>
    setData('remove_photo_ids', removeIds.includes(id) ? removeIds.filter((r) => r !== id) : [...removeIds, id])

  const addPhotos = (files: FileList | null) => {
    if (!files) return
    // Anything past the open slots is dropped rather than refused wholesale.
    const added = Array.from(files).slice(0, Math.max(openSlots, 0))
    setData('driver_car', { ...data.driver_car, new_photos: [...newPhotos, ...added] })
  }

  const dropNewPhoto = (index: number) =>
    setData('driver_car', { ...data.driver_car, new_photos: newPhotos.filter((_, i) => i !== index) })

  return (
    <Box className="border-2 py-8 px-10">
      <Heading size="7">{title}</Heading>
      <Text size="4" as="p" color="gray">
        {subtitle}
      </Text>
      <Separator my="3" size="4" />
      {errors && errors.length > 0 && (
        <Callout.Root color="red" role="alert">
          {errors.map((error, index) => (
            <div key={index}>
              <Callout.Icon>
                <AlertCircle />
              </Callout.Icon>
              <Callout.Text>{error}</Callout.Text>
            </div>
          ))}
        </Callout.Root>
      )}
      <form onSubmit={onSubmit}>
        <Grid columns="1" gap="5" my="5">
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Location</Text>
                <TextField.Root
                  id="location"
                  size="3"
                  value={data.participant.location}
                  onChange={(e) => setData("participant", { ...data.participant, location: e.target.value })}
                  placeholder="Where are you (ish?)"
                >
                  <TextField.Slot>
                    <MapPinHouse height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Email</Text>
                <TextField.Root
                  type="email"
                  size="3"
                  id="email"
                  value={data.participant.email}
                  onChange={(e) => setData("participant", { ...data.participant, email: e.target.value })}
                  placeholder="Better email to reach you at than your primary?"
                >
                  <TextField.Slot>
                    <Mail height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Phone Number</Text>
                <TextField.Root
                  type="text"
                  size="3"
                  id="phone_number"
                  value={data.participant.phone_number}
                  onChange={(e) => setData("participant", { ...data.participant, phone_number: e.target.value })}
                  placeholder="Sometimes it's easier to just call you"
                >
                  <TextField.Slot>
                    <Phone height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Instagram</Text>
                <TextField.Root
                  id="instagram"
                  size="3"
                  value={data.participant.instagram}
                  onChange={(e) => setData("participant", { ...data.participant, instagram: e.target.value })}
                  placeholder="@handle — how photographers find you"
                >
                  <TextField.Slot>
                    <Instagram height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Text>
          </Box>
        </Grid>

        <Text size="6">Car Information</Text>
        <Separator my="3" size="4" />
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Driver Name *</Text>
                <TextField.Root
                  id="driver_name"
                  size="3"
                  value={data.driver_car.driver_name}
                  onChange={(e) => setData("driver_car", { ...data.driver_car, driver_name: e.target.value })}
                  required
                  placeholder="Name on the entry list"
                >
                  <TextField.Slot>
                    <User height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Text>
          </Box>
        <Grid columns={{ initial: '1', sm: '2' }} gap="5" my="5">
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Year</Text>
                <TextField.Root
                  type="number"
                  size="3"
                  id="year"
                  value={data.driver_car.year ?? ''}
                  onChange={(e) => setData("driver_car", { ...data.driver_car, year: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder="e.g. 2003"
                />
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Make *</Text>
                <TextField.Root
                  id="make"
                  size="3"
                  value={data.driver_car.make}
                  onChange={(e) => setData("driver_car", { ...data.driver_car, make: e.target.value })}
                  required
                  placeholder="e.g. Nissan"
                >
                  <TextField.Slot>
                    <Car height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Model *</Text>
                <TextField.Root
                  id="model"
                  size="3"
                  value={data.driver_car.model}
                  onChange={(e) => setData("driver_car", { ...data.driver_car, model: e.target.value })}
                  required
                  placeholder="e.g. 240SX"
                >
                  <TextField.Slot>
                    <Car height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Trim</Text>
                <TextField.Root
                  id="trim"
                  size="3"
                  value={data.driver_car.trim}
                  onChange={(e) => setData("driver_car", { ...data.driver_car, trim: e.target.value })}
                  placeholder="e.g. Hatchback"
                />
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Chassis Code</Text>
                <TextField.Root
                  id="chassis_code"
                  size="3"
                  value={data.driver_car.chassis_code}
                  onChange={(e) => setData("driver_car", { ...data.driver_car, chassis_code: e.target.value })}
                  placeholder="e.g. S13"
                />
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Engine</Text>
                <TextField.Root
                  id="engine"
                  size="3"
                  value={data.driver_car.engine}
                  onChange={(e) => setData("driver_car", { ...data.driver_car, engine: e.target.value })}
                  placeholder="e.g. SR20DET"
                />
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Aspiration</Text>
                <Select.Root
                  size="3"
                  value={data.driver_car.aspiration?.toString() ?? ''}
                  onValueChange={(v) => setData("driver_car", { ...data.driver_car, aspiration: parseInt(v) })}
                >
                  <Select.Trigger id="aspiration" placeholder="Select aspiration..." />
                  <Select.Content>
                    {aspirations.map((option) => (
                      <Select.Item key={option.value} value={option.value.toString()}>{option.label}</Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Charger</Text>
                <TextField.Root
                  id="charger"
                  size="3"
                  value={data.driver_car.charger}
                  onChange={(e) => setData("driver_car", { ...data.driver_car, charger: e.target.value })}
                  placeholder="e.g. GT3076"
                />
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Horsepower</Text>
                <TextField.Root
                  type="number"
                  size="3"
                  id="hp"
                  value={data.driver_car.hp ?? ''}
                  onChange={(e) => setData("driver_car", { ...data.driver_car, hp: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder="e.g. 450"
                >
                  <TextField.Slot>
                    <Gauge height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Torque</Text>
                <TextField.Root
                  type="number"
                  size="3"
                  id="torque"
                  value={data.driver_car.torque ?? ''}
                  onChange={(e) => setData("driver_car", { ...data.driver_car, torque: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder="ft-lbs"
                >
                  <TextField.Slot>
                    <Gauge height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Body Kit</Text>
                <TextField.Root
                  id="body_kit"
                  size="3"
                  value={data.driver_car.body_kit}
                  onChange={(e) => setData("driver_car", { ...data.driver_car, body_kit: e.target.value })}
                  placeholder="e.g. Rocket Bunny"
                />
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Wheels</Text>
                <TextField.Root
                  id="wheels"
                  size="3"
                  value={data.driver_car.wheels}
                  onChange={(e) => setData("driver_car", { ...data.driver_car, wheels: e.target.value })}
                  placeholder="e.g. Work Meister S1"
                />
              </Flex>
            </Text>
          </Box>
        </Grid>

        <Box my="5">
          <Text as="div" size="5" mb="2">Color *</Text>
          <RadioCards.Root
            size="1"
            columns={{ initial: '2', sm: '4' }}
            gap="2"
            required
            value={data.driver_car.color_family}
            onValueChange={(v) => setData("driver_car", { ...data.driver_car, color_family: v })}
          >
            {colorFamilies.map((color) => (
              <RadioCards.Item key={color.value} value={color.value}>
                <Flex align="center" gap="2" width="100%">
                  <ColorSwatch color={color} size={18} />
                  <Text size="2">{color.label}</Text>
                </Flex>
              </RadioCards.Item>
            ))}
          </RadioCards.Root>
        </Box>

        {photos && (
          <>
            <Text size="6">Photos</Text>
            <Separator my="3" size="4" />
            <Text as="p" size="2" color="gray" mb="3">
              Up to {MAX_PHOTOS} photos (JPEG, PNG or WebP). The first one is the cover.
            </Text>
            <Grid columns={{ initial: '2', sm: '3' }} gap="3" mb="3">
              {photos.map((photo) => {
                const removing = removeIds.includes(photo.id)
                return (
                  <Box key={photo.id} position="relative">
                    <img
                      src={photo.thumb_url ?? undefined}
                      alt="Car photo"
                      style={{ display: 'block', width: '100%', height: 140, objectFit: 'cover', opacity: removing ? 0.3 : 1 }}
                    />
                    <Button
                      type="button"
                      size="1"
                      variant="soft"
                      color={removing ? 'gray' : 'crimson'}
                      onClick={() => toggleRemove(photo.id)}
                      style={{ position: 'absolute', top: 6, right: 6 }}
                    >
                      {removing ? 'Undo' : <X height="14" width="14" />}
                    </Button>
                  </Box>
                )
              })}
              {newPreviews.map((url, index) => (
                <Box key={url} position="relative">
                  <img src={url} alt="New car photo" style={{ display: 'block', width: '100%', height: 140, objectFit: 'cover' }} />
                  <Button
                    type="button"
                    size="1"
                    variant="soft"
                    color="crimson"
                    onClick={() => dropNewPhoto(index)}
                    style={{ position: 'absolute', top: 6, right: 6 }}
                  >
                    <X height="14" width="14" />
                  </Button>
                </Box>
              ))}
            </Grid>
            {openSlots > 0 && (
              <Flex align="center" gap="2">
                <ImagePlus height="16" width="16" />
                <input
                  type="file"
                  accept={PHOTO_TYPES}
                  multiple
                  // Cleared after each pick so choosing the same file again still fires.
                  onChange={(e) => { addPhotos(e.target.files); e.target.value = '' }}
                  style={{ fontSize: 'var(--font-size-2)' }}
                />
              </Flex>
            )}
          </>
        )}

        <Box mt="8">
          <Flex gap="4" justify="end" dir="row">
            {cancelPath && (
              <Button type="button" size="3" asChild color="gray" variant="soft">
                <Link href={cancelPath}>Cancel</Link>
              </Button>
            )}
            <Button size="3" type="submit" disabled={processing}>
              {submitLabel}
            </Button>
          </Flex>
        </Box>
      </form>
    </Box>
  )
}
